"use client";

import { useEffect, useState } from "react";
import { PillLinkButton } from "@/components/brand/pill-button";
import { safeNextPath } from "@/lib/auth/safe-next";

/**
 * Zitadel sign-in is a plain redirect to a route handler
 * (packages/zitadel-auth's OIDC/PKCE flow), not
 * `supabase.auth.signInWithOAuth` — flag-gated via /api/auth/providers so it
 * renders nothing while Zitadel is disabled.
 */
export function ZitadelSignInButton({ next }: { next?: string | null }) {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let active = true;
    void fetch("/api/auth/providers", {
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" },
    })
      .then(async (response) => {
        if (!response.ok) return false;
        const payload: unknown = await response.json();
        return (
          typeof payload === "object" &&
          payload !== null &&
          (payload as { zitadel?: unknown }).zitadel === true
        );
      })
      .then((value) => {
        if (active) setEnabled(value);
      })
      .catch(() => {
        if (active) setEnabled(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (!enabled) return null;

  // returnTo points at /auth/continue rather than the real destination
  // directly — that interstitial paints instantly and only THEN navigates
  // on client-side, so the browser always has something to show the moment
  // it lands back from Zitadel (see src/app/auth/continue/page.tsx).
  const safeNext = safeNextPath(next);
  const continueTarget = `/auth/continue${safeNext ? `?to=${encodeURIComponent(safeNext)}` : ""}`;
  const href = `/api/auth/login?returnTo=${encodeURIComponent(continueTarget)}`;

  return (
    <section
      aria-label="Toddoo single sign-on"
      style={{
        borderTop: "1px solid var(--tdo-border)",
        marginTop: "var(--tdo-sp-5)",
        paddingTop: "var(--tdo-sp-5)",
      }}
    >
      <PillLinkButton block variant="secondary" href={href} plainAnchor>
        Continue with Toddoo SSO
      </PillLinkButton>
    </section>
  );
}
